import React, { useEffect, useState } from "react";
import { ArrowUpRight, ArrowRight, CheckCircle2, Clock, Sparkles, Star, X } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const CaseStudy = () => {
  const { t } = useLang();
  const cs = t.case_study;
  const [open, setOpen] = useState(false);

  // Lock body scroll while modal open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const onKey = (e) => e.key === "Escape" && setOpen(false);
      window.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = prev;
        window.removeEventListener("keydown", onKey);
      };
    }
    return undefined;
  }, [open]);

  return (
    <section id="casos" className="py-20 lg:py-28 relative overflow-hidden" data-testid="case-study-section">
      <div className="absolute top-1/3 -left-20 w-[400px] h-[400px] bg-[#E63946]/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 -right-20 w-[400px] h-[400px] bg-[#E8C547]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5A0] mb-3">
            — {cs.eyebrow}
          </div>
          <h2 className="font-display font-extrabold text-[clamp(2rem,4.5vw,3.2rem)] leading-[1.05] tracking-tight">
            {cs.title_a} <span className="text-[#00E5A0]">{cs.title_h}</span>{cs.title_b}
          </h2>
        </div>

        {/* Compact summary card */}
        <button
          type="button"
          data-testid="case-study-open"
          onClick={() => setOpen(true)}
          className="group relative w-full text-left zk-glass rounded-3xl p-3 hover:border-[#00E5A0]/40 transition-all overflow-hidden"
        >
          <div className="grid sm:grid-cols-[200px_1fr_auto] gap-4 items-center">
            {/* Thumbnail */}
            <div className="relative rounded-2xl overflow-hidden aspect-[16/10] bg-black">
              <img src="/mototuning-preview.webp" alt="MotoTuning C.A." loading="lazy"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-[#E63946] via-[#E8C547] to-[#E63946] opacity-70" />
              <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-black/70 backdrop-blur-md rounded-full px-1.5 py-0.5 border border-white/10">
                <span className="w-1 h-1 rounded-full bg-[#00E5A0] animate-pulse" />
                <span className="text-[8px] font-mono uppercase tracking-wider text-white">{t.services.preview_live}</span>
              </div>
            </div>

            {/* Content */}
            <div className="min-w-0 pl-1">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-full grid place-items-center font-display font-extrabold text-[10px] flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #E8C547, #A88A1F)", color: "#0D1B2A" }}>MT</div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-white text-sm leading-tight truncate">MotoTuning C.A.</div>
                  <div className="text-[10px] text-[#6B7A8D] leading-none">Aragua · Venezuela</div>
                </div>
                <div className="ml-auto flex items-center gap-0.5 text-[#00E5A0]">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3 fill-current" />)}
                </div>
              </div>
              <p className="text-[13px] text-[#F5F7FA]/75 leading-snug line-clamp-2 italic">
                &ldquo;{cs.quote.split(".").slice(0, 2).join(".")}.&rdquo;
              </p>
            </div>

            {/* CTA chevron */}
            <div className="hidden sm:flex items-center pr-2">
              <div className="flex items-center gap-1.5 text-sm text-[#00E5A0] font-medium group-hover:gap-2.5 transition-all">
                <span className="hidden md:inline">{cs.visit.split(" ").slice(0,2).join(" ")}</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
          {/* Mobile CTA hint */}
          <div className="sm:hidden mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[12px] text-[#00E5A0]">
            <span>{cs.label_quote}</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </button>

        {/* Next-case prompt */}
        <div className="mt-5 text-center">
          <a href="#contacto" data-testid="case-next-cta"
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-dashed border-[#00E5A0]/40 hover:border-[#00E5A0] hover:bg-[#00E5A0]/5 transition-all group">
            <span className="text-xs text-[#6B7A8D]">{cs.next_label}</span>
            <span className="font-display font-semibold text-white text-sm">{cs.next_cta}</span>
            <ArrowRight className="w-4 h-4 text-[#00E5A0] group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      {/* Modal — detail view */}
      {open && <CaseModal cs={cs} t={t} onClose={() => setOpen(false)} />}
    </section>
  );
};

const CaseModal = ({ cs, t, onClose }) => (
  <div
    role="dialog"
    aria-modal="true"
    data-testid="case-modal"
    className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6 animate-fade-up"
    style={{ animationDuration: "0.25s" }}
  >
    {/* Backdrop */}
    <button
      type="button"
      aria-label="Close"
      data-testid="case-modal-backdrop"
      onClick={onClose}
      className="absolute inset-0 bg-[#0D1B2A]/85 backdrop-blur-md"
    />

    {/* Content */}
    <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto zk-glass rounded-3xl border border-[#00E5A0]/30 shadow-2xl shadow-black/60">
      {/* Close button */}
      <button
        type="button"
        data-testid="case-modal-close"
        onClick={onClose}
        aria-label="Close"
        className="sticky top-3 ml-auto mr-3 flex z-10 w-9 h-9 rounded-full bg-[#0D1B2A] border border-white/10 grid place-items-center text-[#F5F7FA]/70 hover:text-white hover:border-[#00E5A0]/40 transition"
        style={{ float: "right" }}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="p-6 sm:p-8">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5A0] mb-3">
          — {cs.eyebrow}
        </div>
        <h3 className="font-display font-extrabold text-3xl lg:text-4xl leading-[1.05] tracking-tight mb-6">
          {cs.title_a} <span className="text-[#00E5A0]">{cs.title_h}</span>{cs.title_b}
        </h3>

        {/* Cover image */}
        <a href="https://mototuningca.com" target="_blank" rel="noopener noreferrer"
          className="block rounded-2xl overflow-hidden bg-black aspect-[16/9] relative group mb-6 border border-white/5">
          <img src="/mototuning-preview.webp" alt="MotoTuning C.A. site" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-700" />
          <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#E63946] via-[#E8C547] to-[#E63946] opacity-80" />
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-md rounded-full px-2.5 py-1 border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E5A0] animate-pulse" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-white">{t.services.preview_live}</span>
          </div>
          <div className="absolute bottom-3 right-3 bg-[#00E5A0] text-[#0D1B2A] text-[10px] font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
            {cs.visit} <ArrowUpRight className="w-3 h-3" />
          </div>
        </a>

        <p className="text-base text-[#F5F7FA]/65 mb-7">{cs.subtitle}</p>

        {/* Brief */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#00E5A0]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5A0]">{cs.label_brief}</span>
          </div>
          <p className="text-sm text-[#F5F7FA]/80 leading-relaxed">{cs.brief}</p>
        </div>

        {/* Solution */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-[#00E5A0]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5A0]">{cs.label_solution}</span>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
            {cs.solution.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#F5F7FA]/80">
                <span className="text-[#00E5A0] mt-1 text-[10px]">●</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Outcome + Time */}
        <div className="grid sm:grid-cols-[1.6fr_1fr] gap-3 mb-6">
          <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5A0] mb-1.5">{cs.label_outcome}</div>
            <p className="text-sm text-[#F5F7FA]/80 leading-snug">
              <span className="text-white font-semibold">{cs.outcome_a}</span>{cs.outcome_b}
            </p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5 text-[#00E5A0]" />
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5A0]">{cs.label_time}</span>
            </div>
            <div className="font-display font-extrabold text-2xl text-white leading-tight">{cs.time}</div>
          </div>
        </div>

        {/* Quote */}
        <div className="relative bg-[#00E5A0]/5 border-l-[3px] border-[#00E5A0] rounded-r-2xl p-5 mb-6 overflow-hidden">
          <div className="absolute -top-6 -right-2 font-display font-extrabold text-[8rem] leading-none text-[#00E5A0]/10 select-none pointer-events-none">&ldquo;</div>
          <div className="relative">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5A0] mb-2">{cs.label_quote}</div>
            <p className="font-display text-base text-white leading-snug font-medium mb-3">&ldquo;{cs.quote}&rdquo;</p>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full grid place-items-center font-display font-extrabold text-xs"
                style={{ background: "linear-gradient(135deg, #E8C547, #A88A1F)", color: "#0D1B2A" }}>MT</div>
              <div>
                <div className="font-semibold text-white text-sm">MotoTuning C.A.</div>
                <div className="text-xs text-[#6B7A8D]">Aragua · Venezuela</div>
              </div>
            </div>
          </div>
        </div>

        {/* Visit CTA */}
        <a href="https://mototuningca.com" target="_blank" rel="noopener noreferrer"
          data-testid="case-modal-visit"
          className="zk-shine w-full inline-flex items-center justify-center gap-2 bg-[#00E5A0] text-[#0D1B2A] font-bold px-6 py-3.5 rounded-full hover:shadow-[0_20px_50px_-10px_rgba(0,229,160,0.5)] transition-all">
          {cs.visit}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </div>
);

export default CaseStudy;
